import PageWrapper from "@/components/PageWrapper";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorHandler } from "@/components/error-hanlder/ErrorHandler";
import { logError } from "@/components/error-hanlder/error-handler.lib";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import {
  useLessonDetailOptions,
  useSubmitTestMutation,
  useStartTestMutation,
  useTestDetailOptions,
  TestingKeys,
} from "./testing.api";
import {
  AnswerSubmit,
  ImageType,
  Question,
  QuestionResult,
  TestDetail,
  TestResult,
} from "./testing.types";
import { useTranslation } from "react-i18next";
import BaseButton from "@/components/base/button/BaseButton";
import MultipleChoice from "@/pages/get-don/components/tests/test-types/MultipleChoise";
import Checkbox from "@/pages/get-don/components/tests/test-types/CheckBox";
import Ordering from "@/pages/get-don/components/tests/test-types/Ordering";
import ImageBased from "@/pages/get-don/components/tests/test-types/ImageBased";
import FinishTest from "@/pages/get-don/components/tests/FinishTest";
import showCustomToast from "@/shared/utils/showCustomToast";
import { boom } from "@/lib/exposion";
import { useAnimationStore } from "@/components/common/explosion/exposion.store";
import GetDoneSuccessIcon from "@/assets/icons/getdon_success.svg?react";
import { formatNumber } from "@/shared/utils";
import { VideoPlayerModal } from "./LessonListPage";
import IconCheck from "@/assets/icons/check.svg?react";

// ─── Utilities ───

const formatSecondsToTime = (seconds: number) => ({
  mins: String(Math.floor(seconds / 60)).padStart(2, "0"),
  secs: String(seconds % 60).padStart(2, "0"),
});

// ─── Shared Components ───

const OrderingList =({ ids, optMap, style }: { ids: string[]; optMap: Record<string, { text: string | null }>; style: string }) => (
  <div className="flex flex-col gap-1.5">
    {ids.map((id, index) => (
      <div key={String(id)} className={`py-2.5 px-3 rounded-xl border ${style} flex items-start gap-3`}>
        <span className="flex-center size-6 shrink-0 rounded-full bg-white/[0.08] text-xs font-semibold text-white/80">
          {index + 1}
        </span>
        <p className="text-sm font-semibold text-white text-start leading-5">{optMap[String(id)]?.text || String(id)}</p>
      </div>
    ))}
  </div>
);

const QuestionVideo = ({ video }: { video: ImageType | null }) => {
  const [showPlayer, setShowPlayer] = useState(false);
  if (!video) return null;

  return (
    <>
      {/* Thumbnail with play button */}
      <div
        className="relative rounded-xl overflow-hidden mt-3 cursor-pointer active:opacity-80"
        onClick={(e) => { e.stopPropagation(); setShowPlayer(true); }}
      >
        <video
          src={video.url}
          preload="metadata"
          playsInline
          muted
          className="w-full mb-3 aspect-video object-cover rounded-xl"
        />
        {/* Play overlay */}
        <div className="absolute inset-0 mb-3 flex items-center justify-center bg-black/30">
          <div className="w-12 h-12 rounded-full bg-white/25 flex-center">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="size-6 ml-1">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          </div>
        </div>
      </div>

      {/* Fullscreen player — LessonListPage dagi VideoPlayerModal bilan bir xil */}
      {showPlayer && (
        <VideoPlayerModal videoUrl={video.url} onClose={() => setShowPlayer(false)} />
      )}
    </>
  );
};

const TestTakingPage = () => {
  return (
    <ErrorBoundary FallbackComponent={ErrorHandler} onError={logError}>
      <Suspense fallback={<TestLoading />}>
        <BaseTestTakingPage />
      </Suspense>
    </ErrorBoundary>
  );
};

const TestLoading = () => (
  <PageWrapper>
    <div className="pt-2 pb-24">
      {/* Number bar skeleton */}
      <div className="flex gap-1.5 px-4 pb-3 overflow-x-auto scrollbar-hide">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className={`skeleton w-8 h-8 rounded-lg shrink-0 ${i === 0 ? "opacity-100" : "opacity-40"}`} />
        ))}
      </div>

      <div className="container">
        {/* Question card skeleton */}
        <div className="p-3 w-full rounded-xl bg-gray-4 mb-1">
          <div className="flex items-center mb-2 justify-between">
            <div className="skeleton w-24 h-5 rounded-md" />
            <div className="flex items-center gap-0.5">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="skeleton w-[22px] h-[22px] rounded-sm" />
              ))}
            </div>
          </div>
          <div className="w-full mb-3">
            <div className="bg-white/10 w-full h-1.5 rounded-[10px]" />
          </div>
          <div className="skeleton w-full h-5 rounded-md mb-1" />
          <div className="skeleton w-3/4 h-5 rounded-md" />
        </div>

        {/* Question type label skeleton */}
        <div className="skeleton w-40 h-3 rounded-md mt-3 mb-1" />

        {/* Answer options skeleton — aynan CheckBox component structurasi */}
        <div className="flex flex-col items-center w-full justify-center gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="p-px rounded-xl w-full bg-transparent">
              <div className="bg-gray-4 rounded-xl">
                <div className="py-2.5 px-3 w-full rounded-xl bg-gray-4">
                  <div className="flex items-center justify-between w-full gap-3">
                    <div className="skeleton h-4 rounded-md" style={{ width: `${50 + i * 12}%` }} />
                    <div className="skeleton w-6 h-6 rounded-full shrink-0" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom buttons skeleton */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#0E0E14] p-4 pb-safe-bottom flex gap-2">
        <div className="skeleton flex-1 h-[46px] rounded-xl" />
        <div className="skeleton flex-1 h-[46px] rounded-xl" />
      </div>
    </div>
  </PageWrapper>
);

type TestPhase = "taking" | "result" | "review";

const BaseTestTakingPage = () => {
  const { lessonId, testId: initialTestId } = useParams<{ lessonId: string; testId: string }>();
  const [searchParams] = useSearchParams();
  const collectionId = searchParams.get("collection");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Lesson detail for tests list
  const { data: lesson } = useSuspenseQuery(useLessonDetailOptions(lessonId));
  const tests = (lesson?.tests || []).filter((te) => te.questions_count > 0);

  // Multi-test state
  const [currentTestIndex, setCurrentTestIndex] = useState(() => {
    const idx = tests.findIndex((te) => te.id === initialTestId);
    return idx >= 0 ? idx : 0;
  });
  const [phase, setPhase] = useState<TestPhase>("taking");
  const [currentResult, setCurrentResult] = useState<TestResult | null>(null);
  const [allResults, setAllResults] = useState<TestResult[]>([]);
  const [totalElapsed, setTotalElapsed] = useState(0);
  const [reviewData, setReviewData] = useState<{ result: TestResult; questions: Question[] } | null>(null);

  const currentTest = tests[currentTestIndex];

  // Current test detail
  const { data: testDetail } = useSuspenseQuery(useTestDetailOptions(currentTest?.id));

  // Mutations
  const { mutateAsync: submitTest, isPending: isSubmitting } = useSubmitTestMutation();
  const { mutateAsync: startTest } = useStartTestMutation();

  const backPath = `/testing${collectionId ? `?collection=${collectionId}` : ""}`;

  // Auto-start test if no active attempt exists (e.g. page refresh, expired attempt)
  useEffect(() => {
    if (testDetail && !testDetail.attempt_id && currentTest && phase === "taking") {
      startTest(currentTest.id).then(() => {
        queryClient.invalidateQueries({ queryKey: [TestingKeys.TestDetail, currentTest.id] });
      }).catch(() => {
        showCustomToast(t("test_start_error"), "error");
      });
    }
  }, [testDetail?.attempt_id, currentTest?.id, phase]);

  const processResult = (res: TestResult) => {
    // Elapsed time for THIS test = its attempt's completed_at - started_at.
    // Read started_at from the current testDetail (the attempt being
    // submitted) rather than a cached value, which could be stale from an
    // earlier attempt and wildly inflate the reported time.
    const startedAt = testDetail?.started_at;
    if (res.completed_at && startedAt) {
      const elapsed = Math.max(0, Math.floor(
        (new Date(res.completed_at).getTime() - new Date(startedAt).getTime()) / 1000
      ));
      setTotalElapsed((prev) => prev + elapsed);
    }

    setCurrentResult(res);
    setAllResults((prev) => [...prev, res]);
    setPhase("result");

    // Celebration on pass
    if (res.passed) {
      boom();
    }
    if (res.don_awarded && Number(res.don_award_amount) > 0) {
      showCustomToast(t("congratulations"), "success", {
        description: t("don_awarded_test", { amount: formatNumber(Number(res.don_award_amount)) }),
        icon: <GetDoneSuccessIcon className="size-7" />,
      });
    }

    // Invalidate lists and profile (wallet balance)
    queryClient.invalidateQueries({ queryKey: [TestingKeys.LessonList] });
    queryClient.invalidateQueries({ queryKey: [TestingKeys.LessonDetail, lessonId] });
    queryClient.invalidateQueries({ queryKey: ["profile"] });
  };

  const handleSubmit = async (answers: AnswerSubmit[]) => {
    if (!currentTest) return;
    try {
      const res = await submitTest({ testId: currentTest.id, answers });
      processResult(res);
    } catch (error) {
      const detail =
        (error as { response?: { data?: { detail?: unknown } } })?.response
          ?.data?.detail ?? "";
      // If no active attempt, auto-restart and retry submit
      if (typeof detail === "string" && detail.toLowerCase().includes("no active")) {
        try {
          await startTest(currentTest.id);
          const res = await submitTest({ testId: currentTest.id, answers });
          processResult(res);
        } catch {
          showCustomToast(t("submit_error"), "error");
        }
      } else {
        showCustomToast(t("submit_error"), "error");
      }
    }
  };

  const handleReview = () => {
    if (currentResult && testDetail) {
      useAnimationStore.getState().hideAnimation();
      setReviewData({ result: currentResult, questions: testDetail.questions });
      setPhase("review");
    }
  };

  if (!lesson || tests.length === 0) {
    return (
      <PageWrapper backPath={backPath}>
        <div className="container pt-10 text-center text-gray-1">
          {t("test_not_found")}
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper backPath={backPath}>
      {phase === "taking" && testDetail && testDetail.questions.length === 0 && (
        <div className="container pt-10 text-center">
          <p className="text-gray-1 text-sm">Savollar hali qo'shilmagan</p>
          <BaseButton text={t("back")} className="mt-4" onClick={() => navigate(backPath)} />
        </div>
      )}
      {phase === "taking" && testDetail && testDetail.questions.length > 0 && (
        <TestQuestionPhase
          testDetail={testDetail}
          deadline={testDetail.deadline}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
      )}
      {phase === "result" && currentResult && (
        <FinalResult
          allResults={allResults}
          totalElapsed={totalElapsed}
          maxDon={Number(lesson?.max_test_don) || 0}
          onReview={handleReview}
          onBack={() => navigate(backPath)}
          onRetry={async () => {
            // Testni birinchidan qayta boshlash
            const firstTest = tests[0];
            if (firstTest) {
              await startTest(firstTest.id);
              queryClient.removeQueries({ queryKey: [TestingKeys.TestDetail, firstTest.id] });
              setCurrentTestIndex(0);
              setPhase("taking");
              setCurrentResult(null);
              setAllResults([]);
              setTotalElapsed(0);
              setReviewData(null);
            }
          }}
        />
      )}
      {phase === "review" && reviewData && (
        <TestReviewPhase
          result={reviewData.result}
          questions={reviewData.questions}
          onBack={() => setPhase("result")}
        />
      )}
    </PageWrapper>
  );
};

// ─── Question Phase ───

interface TestQuestionPhaseProps {
  testDetail: TestDetail;
  deadline: string | null;
  onSubmit: (answers: AnswerSubmit[]) => void;
  isSubmitting: boolean;
}

const TestQuestionPhase = ({ testDetail, deadline, onSubmit, isSubmitting }: TestQuestionPhaseProps) => {
  const { t } = useTranslation();
  const storageKey = `test_answers_${testDetail.id}`;

  const [currentIndex, setCurrentIndex] = useState(() => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      return saved ? JSON.parse(saved).currentIndex || 0 : 0;
    } catch { return 0; }
  });

  const [answers, setAnswers] = useState<Record<string, AnswerSubmit>>(() => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      return saved ? JSON.parse(saved).answers || {} : {};
    } catch { return {}; }
  });

  // Javoblar va pozitsiyani sessionStorage ga saqlash
  useEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify({ answers, currentIndex }));
  }, [answers, currentIndex, storageKey]);
  const numberBarRef = useRef<HTMLDivElement>(null);
  const [timeLeft, setTimeLeft] = useState(() => {
    if (deadline) {
      return Math.max(0, Math.floor((new Date(deadline).getTime() - Date.now()) / 1000));
    }
    return testDetail.duration_minutes * 60;
  });

  const questions = testDetail.questions;
  const currentQuestion = questions[currentIndex];
  const totalQuestions = questions.length;

  const handleFinish = useCallback(() => {
    sessionStorage.removeItem(storageKey);
    const allAnswers = questions.map((q) => {
      const ans = answers[q.id];
      return ans || { question_id: q.id };
    });
    onSubmit(allAnswers);
  }, [questions, answers, onSubmit]);

  useEffect(() => {
    if (timeLeft <= 0) {
      handleFinish();
      return;
    }
    const interval = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [timeLeft, handleFinish]);

  const setAnswer = useCallback(
    (questionId: string, answer: Partial<AnswerSubmit>) => {
      setAnswers((prev) => ({
        ...prev,
        [questionId]: { ...prev[questionId], ...answer, question_id: questionId },
      }));
    },
    []
  );

  const goToQuestion = (idx: number) => setCurrentIndex(idx);
  const isLastQuestion = currentIndex === totalQuestions - 1;
  const progress = ((currentIndex + 1) / totalQuestions) * 100;

  const minutes = String(Math.floor(timeLeft / 60)).padStart(2, "0");
  const seconds = String(timeLeft % 60).padStart(2, "0");

  // Scroll number bar to active
  useEffect(() => {
    if (numberBarRef.current) {
      const activeBtn = numberBarRef.current.children[currentIndex] as HTMLElement;
      if (activeBtn) {
        activeBtn.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
      }
    }
  }, [currentIndex]);

  if (!currentQuestion) return null;

  const questionTypeLabel = (() => {
    switch (currentQuestion.question_type) {
      case "CHECKBOX": return t("select_one_answer");
      case "MULTIPLE_CHOICE": return t("select_correct_answers");
      case "IMAGE_CHOICE": return t("select_one_image");
      case "ORDERING": return t("order_correctly");
      default: return "";
    }
  })();

  return (
    <div className="pt-2 pb-24">
      {/* Question number bar */}
      <div ref={numberBarRef} className="flex gap-1.5 px-4 pb-3 overflow-x-auto scrollbar-hide">
        {questions.map((_, idx) => {
          const isActive = idx === currentIndex;
          const isAnswered = !!answers[questions[idx].id];
          return (
            <button
              key={idx}
              onClick={() => goToQuestion(idx)}
              className={`w-9 h-9 rounded-10 text-sm font-semibold shrink-0 transition-all ${
                isActive
                  ? "bg-green-1 border border-white/30 text-white"
                  : isAnswered
                  ? "bg-green-1/20 text-green-1 border border-green-1/40"
                  : "bg-white/08 text-gray-1"
              }`}
            >
              {idx + 1}
            </button>
          );
        })}
      </div>

      <div className="container">

        {/* Question card with timer */}
        <div className="p-3 w-full rounded-xl bg-gray-4 mb-4">
          <div className="flex items-center mb-2 justify-between">
            <h1 className="text-white text-lg leading-130 font-semibold">
              {t("question")} {currentIndex + 1}/{totalQuestions}
            </h1>
            <div className="flex items-center justify-end gap-0.5">
              {minutes.split("").map((d, i) => (
                <p key={`m${i}`} className="py-1 px-1.5 w-[22px] h-[22px] rounded-sm leading-130 bg-green-2/12 text-green-1 text-sm font-semibold flex items-center justify-center">{d}</p>
              ))}
              <span className="text-green-1 text-base font-bold">:</span>
              {seconds.split("").map((d, i) => (
                <p key={`s${i}`} className="py-1 px-1.5 w-[22px] h-[22px] rounded-sm leading-130 bg-green-2/12 text-green-1 text-sm font-semibold flex items-center justify-center">{d}</p>
              ))}
            </div>
          </div>
          <div className="w-full mb-3">
            <div className="bg-gray-5 w-full h-1.5 rounded-[10px]">
              <div style={{ width: `${progress}%` }} className="h-1.5 rounded-[10px] bg-green-1 transition-all" />
            </div>
          </div>
          <h2 className="leading-140 text-lg font-semibold text-white">{currentQuestion.text}</h2>

          {/* Question video — card ichida */}
          <QuestionVideo video={currentQuestion.video} />

          {/* Question image — card ichida */}
          {currentQuestion.image && (
            <div className="rounded-xl overflow-hidden mt-3">
              <img src={currentQuestion.image.url} className="w-full object-cover" alt="" />
            </div>
          )}
        </div>

        {/* Question type label */}
        <p className="text-sm font-medium text-gray-1 mb-3">{questionTypeLabel}</p>

        {/* Answer options */}
        <QuestionAnswerArea
          question={currentQuestion}
          answer={answers[currentQuestion.id]}
          onAnswer={(ans) => setAnswer(currentQuestion.id, ans)}
        />
      </div>

      {/* Navigation buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#0E0E14] p-4 pb-safe-bottom flex gap-2">
        <BaseButton
          text={t("back")}
          className="flex-1 bg-white/08"
          onClick={() => currentIndex > 0 ? setCurrentIndex(currentIndex - 1) : null}
          disabled={currentIndex === 0}
        />
        {isLastQuestion ? (
          <BaseButton
            text={t("finish_test")}
            variant="premium"
            className="flex-1 text-black"
            onClick={handleFinish}
            isLoading={isSubmitting}
          />
        ) : (
          <BaseButton
            text={t("continue")}
            className="flex-1"
            onClick={() => setCurrentIndex(currentIndex + 1)}
          />
        )}
      </div>
    </div>
  );
};

// ─── Answer Area ───

interface QuestionAnswerAreaProps {
  question: Question;
  answer?: AnswerSubmit;
  onAnswer: (answer: Partial<AnswerSubmit>) => void;
}

const QuestionAnswerArea = ({ question, answer, onAnswer }: QuestionAnswerAreaProps) => {
  const options = question.options.map((o) => ({ id: o.id, title: o.text || "" }));

  switch (question.question_type) {
    case "CHECKBOX":
      return (
        <Checkbox
          options={options}
          value={answer?.selected_option_id || ""}
          onChange={(id) => onAnswer({ selected_option_id: id })}
        />
      );
    case "MULTIPLE_CHOICE":
      return (
        <MultipleChoice
          options={options}
          value={answer?.selected_option_ids || []}
          onChange={(ids) => onAnswer({ selected_option_ids: ids })}
        />
      );
    case "IMAGE_CHOICE":
      return (
        <ImageBased
          options={question.options.map((o) => ({
            id: o.id,
            title: o.text || "",
            image: o.image?.url || null,
          }))}
          value={answer?.selected_option_id || ""}
          onChange={(id) => onAnswer({ selected_option_id: id })}
        />
      );
    case "ORDERING":
      return (
        <Ordering
          options={options}
          value={answer?.ordering_answer?.join(",") || options.map((o) => o.id).join(",")}
          onChange={(val) => onAnswer({ ordering_answer: val.split(",") })}
        />
      );
    default:
      return null;
  }
};


// ─── Final Result (after all tests) ───

interface FinalResultProps {
  allResults: TestResult[];
  totalElapsed: number;
  maxDon: number;
  onReview: () => void;
  onBack: () => void;
  onRetry: () => void;
}

const FinalResult = ({ allResults, totalElapsed, maxDon, onReview, onBack, onRetry }: FinalResultProps) => {
  const { t } = useTranslation();
  const [donClaimed, setDonClaimed] = useState(false);
  const totalCorrect = allResults.reduce((sum, r) => sum + r.correct_answers, 0);
  const totalQuestions = allResults.reduce((sum, r) => sum + r.total_questions, 0);
  const score = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;

  // DON earned = what the backend actually granted on passing the test
  // (Test.don_award, once). The section's full reward (max_test_don) is shown
  // greyed out on fail.
  const earnedDon = Math.round(allResults.reduce((sum, r) => sum + Number(r.don_award_amount || 0), 0));
  // Pass/fail is decided by the SCORE alone (backend `passed`, i.e. >= 70%).
  // DON is a separate once-per-module reward: a passing retake (already
  // claimed) still passes but earns 0 — it must NOT be shown as a fail.
  const passed = allResults.some((r) => r.passed);

  // Success shows the DON just earned; if already claimed (or on fail) fall
  // back to the section's full reward (max_test_don) — greyed out on fail.
  const displayDon = earnedDon > 0 ? earnedDon : Math.round(Number(maxDon) || 0);

  const { mins, secs } = formatSecondsToTime(totalElapsed);
  const timeStr = `${mins}:${secs}`;

  const handleGetDon = () => {
    if (donClaimed) return;
    setDonClaimed(true);
    if (earnedDon > 0) {
      boom();
      showCustomToast(t("congratulations"), "success", {
        description: t("don_awarded_test", { amount: formatNumber(earnedDon) }),
        icon: <GetDoneSuccessIcon className="size-7" />,
      });
    }
    setTimeout(() => onBack(), 1500);
  };

  return (
    <FinishTest
      id={0}
      time={timeStr}
      result={`${totalCorrect} / ${totalQuestions}`}
      collected_don={formatNumber(displayDon)}
      result_percent={`${score}%`}
      status={passed ? "success" : "fail"}
      onReview={onReview}
      onBack={handleGetDon}
      onRetry={onRetry}
    />
  );
};

// ─── Review Phase ───

interface TestReviewPhaseProps {
  result: TestResult;
  questions: Question[];
  onBack: () => void;
}

const TestReviewPhase = ({ result, questions, onBack }: TestReviewPhaseProps) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const numberBarRef = useRef<HTMLDivElement>(null);

  const questionResults = result.questions;
  const currentResult = questionResults[currentIndex];
  const currentQuestion = questions.find((q) => q.id === currentResult?.question_id);

  useEffect(() => {
    if (numberBarRef.current) {
      const activeBtn = numberBarRef.current.children[currentIndex] as HTMLElement;
      if (activeBtn) {
        activeBtn.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
      }
    }
  }, [currentIndex]);

  if (!currentResult || !currentQuestion) return null;

  return (
    <div className="pt-2 pb-24">
      {/* Number bar with correct/incorrect colors */}
      <div ref={numberBarRef} className="flex gap-1.5 px-4 pb-3 overflow-x-auto scrollbar-hide">
        {questionResults.map((qr, idx) => {
          const isActive = idx === currentIndex;
          return (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-9 h-9 rounded-10 text-sm font-semibold shrink-0 transition-all border ${
                isActive
                  ? qr.is_correct
                    ? "bg-green-1 text-white border-green-1"
                    : "bg-red-500 text-white border-red-500"
                  : qr.is_correct
                  ? "bg-green-1/20 text-green-1 border-green-1/40"
                  : "bg-red-500/20 text-red-400 border-red-500/40"
              }`}
            >
              {idx + 1}
            </button>
          );
        })}
      </div>

      <div className="container">
        {/* Question text */}
        <div className="p-3 rounded-xl bg-gray-4 mb-3">
          <h2 className="leading-140 text-base font-semibold text-white">{currentQuestion.text}</h2>
        </div>

        {/* Question image */}
        {currentQuestion.image && (
          <div className="rounded-xl overflow-hidden mb-3">
            <img src={currentQuestion.image.url} className="w-full object-cover" alt="" />
          </div>
        )}

        {/* Question video */}
        <QuestionVideo video={currentQuestion.video} />

        {/* Show answer based on type */}
        {currentQuestion.question_type === "ORDERING" ? (
          <OrderingReview currentResult={currentResult} currentQuestion={currentQuestion} />
        ) : (
          <OptionsReview currentResult={currentResult} currentQuestion={currentQuestion} />
        )}
      </div>

      {/* Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#0E0E14] p-4 pb-safe-bottom flex gap-2">
        <BaseButton text={t("back")} className="flex-1 bg-white/08" onClick={onBack} />
        {currentIndex < questionResults.length - 1 ? (
          <BaseButton text={t("continue")} className="flex-1" onClick={() => setCurrentIndex(currentIndex + 1)} />
        ) : (
          <BaseButton text={t("back")} className="flex-1" onClick={onBack} />
        )}
      </div>
    </div>
  );
};

// ─── Review Helpers ───

const OptionsReview = ({ currentResult, currentQuestion }: { currentResult: QuestionResult; currentQuestion: Question }) => {
  const { t } = useTranslation();
  const userSelected = new Set(currentResult.user_answer_option_ids.map(String));
  const correctSet = new Set(currentResult.correct_option_ids.map(String));
  const isImageType = currentQuestion.question_type === "IMAGE_CHOICE";

  if (isImageType) {
    return (
      <div className="grid grid-cols-2 gap-2">
        {currentQuestion.options.map((opt) => {
          const isUserSelected = userSelected.has(String(opt.id));
          const isCorrect = correctSet.has(String(opt.id));

          let style = "border-white/08 bg-gray-4";
          let radioStyle = "border-white/08 bg-gray-4/50 backdrop-blur-sm";
          if (isUserSelected && isCorrect) {
            style = "border-green-1 bg-green-1/[0.08] backdrop-blur-[10px]";
            radioStyle = "bg-green-1 border-green-1";
          } else if (isUserSelected && !isCorrect) {
            style = "border-[#FF3535] bg-[#FF3535]/[0.08] backdrop-blur-[10px]";
            radioStyle = "bg-[#FF3535] border-[#FF3535]";
          } else if (isCorrect) {
            style = "border-green-1 bg-gray-4 backdrop-blur-[10px]";
            radioStyle = "border-green-1 bg-gray-4/50 backdrop-blur-sm";
          }

          return (
            <div key={opt.id} className={`rounded-xl border-2 ${style} overflow-hidden relative`}>
              {/* Radio indicator */}
              <div className={`absolute top-3 right-3 z-10 w-6 h-6 rounded-full border flex items-center justify-center shadow-sm ${radioStyle}`}>
                {isUserSelected && <div className="w-3 h-3 rounded-full bg-white" />}
              </div>
              {opt.image && (
                <img src={opt.image.url} className="w-full aspect-square object-cover" alt="" />
              )}
              {opt.text && (
                <p className="text-xs font-semibold text-white p-2 text-start">{opt.text}</p>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  const isMultiple = currentQuestion.question_type === "MULTIPLE_CHOICE";

  return (
    <div className="flex flex-col gap-1.5">
      {currentQuestion.options.map((opt) => {
        const isUserSelected = userSelected.has(String(opt.id));
        const isCorrect = correctSet.has(String(opt.id));

        let style = "border-white/08 bg-gray-4";
        let indicatorStyle = "border-white/08";
        if (isUserSelected && isCorrect) {
          style = "border-green-1 bg-green-1/[0.08] backdrop-blur-[10px]";
          indicatorStyle = "bg-green-1 border-green-1";
        } else if (isUserSelected && !isCorrect) {
          style = "border-[#FF3535] bg-[#FF3535]/[0.08] backdrop-blur-[10px]";
          indicatorStyle = "bg-[#FF3535] border-[#FF3535]";
        } else if (isCorrect) {
          style = "border-green-1 bg-gray-4 backdrop-blur-[10px]";
          indicatorStyle = "border-green-1";
        }

        return (
          <div key={opt.id} className={`py-2.5 px-3 rounded-xl border ${style} flex items-start justify-between gap-3`}>
            <p className="text-sm font-semibold text-white text-start">{opt.text}</p>
            {isMultiple ? (
              /* Checkbox — to'rtburchak, MultipleChoise stilida */
              <div className={`w-5 h-5 rounded-sm border flex items-center justify-center shrink-0 ${indicatorStyle}`}>
                {isUserSelected && <IconCheck className="w-3.5 h-3.5 text-white" />}
              </div>
            ) : (
              /* Radio — dumaloq, CheckBox stilida */
              <div className={`w-6 h-6 rounded-full border flex items-center justify-center shrink-0 ${indicatorStyle}`}>
                {isUserSelected && <div className="w-3 h-3 rounded-full bg-white" />}
              </div>
            )}
          </div>
        );
      })}
      {!currentResult.is_correct && (
        <p className="text-xs text-green-1 mt-1">
          {t("correct_answer")}: {currentQuestion.options.filter((o) => correctSet.has(String(o.id))).map((o) => o.text).join(", ")}
        </p>
      )}
    </div>
  );
};

const OrderingReview = ({ currentResult, currentQuestion }: { currentResult: QuestionResult; currentQuestion: Question }) => {
  const { t } = useTranslation();
  const optMap = Object.fromEntries(currentQuestion.options.map((o) => [String(o.id), o]));

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-xs text-gray-1 mb-2">{t("selected_answer")}:</p>
        <OrderingList ids={currentResult.user_answer_order} optMap={optMap} style={currentResult.is_correct ? "border-green-1 bg-green-1/[0.08] backdrop-blur-[10px]" : "border-[#FF3535] bg-[#FF3535]/[0.08] backdrop-blur-[10px]"} />
      </div>
      {!currentResult.is_correct && (
        <div>
          <p className="text-xs text-gray-1 mb-2">{t("correct_answer")}:</p>
          <OrderingList ids={currentResult.correct_order} optMap={optMap} style="border-green-1 bg-green-1/[0.08] backdrop-blur-[10px]" />
        </div>
      )}
    </div>
  );
};

export default TestTakingPage;



